import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  defaultDataIdFromObject,
} from "@apollo/client";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import mainPossibleTypes from "common/types/mainApolloPossibleTypes";
import supergraphPossibleTypes from "common/types/federation/supergraphApolloPossibleTypes";
import { typePolicies } from "./typePolicies";
import { getLink } from "./utils";

if (process.env.NODE_ENV !== "production") {
  loadErrorMessages();
  loadDevMessages();
}

/**
 * Portal client supports hitting both networkedActivation, and portal endpoints
 * in order to support unauthenticated requests for login and account activation.
 *
 * @NOTE - Possible types for federated schemas are handled separately. See federation-gateway-client.ts.
 * @TODO - The networkedActivation endpoint will be renamed to be more clear about its function.
 */
const portalCache = new InMemoryCache({
  possibleTypes: mainPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies,
});

/**
 * @NOTE - Possible types for federated schemas are handled separately. See federation-gateway-client.ts.
 */
const staffCache = new InMemoryCache({
  possibleTypes: mainPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies,
});
const quarterlyDataUploadCache = new InMemoryCache({
  possibleTypes: mainPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies,
});
const kycInviteCache = new InMemoryCache({
  possibleTypes: mainPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies,
});
const networkedActivationCache = new InMemoryCache({
  possibleTypes: mainPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies,
});

const portalLink = getLink("/graphql/portal", { graphqlClientName: "main-portal" });
const networkedActivationLink = getLink("/graphql/networked_activation", {
  graphqlClientName: "main-networked-activation",
});

/**
 * Directional Composition of Apollo Links to route requests to the federation instance.
 * Use .split() function to route queries to the correct link.
 * By default the `link` parameter will be used, but if a query is made with
 * the {context: {unauthenticated: true}} option, the `networkedActivationLink` will be used.
 * This allows for querying separate endpoints from a single ApolloClient instance.
 *
 * @see https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition
 */
const withNetworkedActivationLink = (link: ApolloLink) => {
  return ApolloLink.split(
    (operation) => operation.getContext().unauthenticated === true,
    networkedActivationLink,
    link,
  );
};

export const lpPortalClient = new ApolloClient({
  // @note: importing the whole schema is expensive.
  // Do it only when there's a strong need for that
  cache: portalCache,
  link: withNetworkedActivationLink(portalLink),
});

export const networkedActivationClient = new ApolloClient({
  cache: networkedActivationCache,
  link: networkedActivationLink,
});

const staffLink = getLink("/graphql/staff", { graphqlClientName: "main-staff" });
export const staffClient = new ApolloClient({
  cache: staffCache,
  link: withNetworkedActivationLink(staffLink),
});

const quarterlyDataUploadLink = getLink("/graphql/quarterly_data_upload", {
  graphqlClientName: "main-quarterly-data-upload",
});
export const quarterlyDataUploadClient = new ApolloClient({
  cache: quarterlyDataUploadCache,
  link: quarterlyDataUploadLink,
});

const kycInviteLink = getLink("/graphql/kyc_invite", { graphqlClientName: "main-kyc-invite" });
export const kycInviteClient = new ApolloClient({
  cache: kycInviteCache,
  link: kycInviteLink,
});

const controlTowerCache = new InMemoryCache({
  possibleTypes: supergraphPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
});

const controlTowerStaffLink = getLink("/graphql/staff", {
  graphqlClientName: "main-control-tower",
});
const controlTowerFederationLink = getLink("/service/federation/graphql", {
  useTerminatingBatchLink: false,
  includeOutlookLink: false,
  graphqlClientName: "main-control-tower",
});

const controlTowerLink = ApolloLink.split(
  (operation) => operation.getContext().clientName === "staff",
  controlTowerStaffLink,
  controlTowerFederationLink,
);

export const controlTowerClient = new ApolloClient({
  cache: controlTowerCache,
  link: controlTowerLink,
});
